import { useEffect, useState } from "react";

const MINUTE_MS = 60 * 1000;

/** Now, rounded down to the current minute. */
export function currentMinute(): number {
  return Math.floor(Date.now() / MINUTE_MS) * MINUTE_MS;
}

/**
 * A clock that ticks once a minute, for deriving relative time windows.
 *
 * Two failure modes this exists to prevent, both seen in this app:
 *
 * 1. Computing the window **once at mount** freezes it. A tab left open all day
 *    keeps asking for "the last 24 hours" as of whenever it was opened.
 *
 * 2. Computing it **raw during render** (`Date.now()`) is worse. The derived
 *    timestamp feeds a react-query `queryKey`, so a value that moves on every
 *    render produces a new key on every render: nothing ever hits the cache and
 *    the query refetches in a loop. On Call History that made the 24h and 7d
 *    presets look broken, while "All" — whose filter object carries no
 *    timestamp — worked fine.
 *
 * Rounding to the minute gives a value stable across renders but still tracking
 * the clock. Feed it to a `useMemo` and derive the ISO strings from that.
 */
export function useCurrentMinute(): number {
  const [now, setNow] = useState(currentMinute);
  useEffect(() => {
    const id = setInterval(() => setNow(currentMinute()), MINUTE_MS);
    return () => clearInterval(id);
  }, []);
  return now;
}
